import React, { Fragment } from 'react';
import { Article } from '../../../../../Components/Article/Article';
import CardHeader from './CardHeader';
import Metric from '../Metric';
import TableMetric from '../TableMetric';
import SentenceGenerator from '../SentenceGenerator';
import { FindingDataItem, AiInsight } from '../../context/AiInsightsApiTypesServices';
import styles from './Card.module.css';
import { getPeriodRange, getIntervalLabelsForData, getComparisonInterval, getFormattedDate } from '../../utils';
import MiniChart from '../../List/MiniChart';
import MiniChart2 from '../../List/MiniChart2';
import MiniBarChart from '../../List/MiniBarChart';
import moment from 'moment';

interface InsightsCardProps {
	insight: AiInsight;
	chartType: string;
}

const InsightsCard: React.FC<InsightsCardProps> = ({
	insight,
	chartType
}) => {

	const range = getPeriodRange(insight.date, insight.period);
	const getBestSegment = (segmentsStats?: FindingDataItem['segmentsStats']) => {
		if (!segmentsStats || segmentsStats.length === 0) return undefined;
		if (segmentsStats.length === 1) return segmentsStats[0];

		const first = segmentsStats[0];
		const last = segmentsStats[segmentsStats.length - 1];
		return (first.referenceValueComparisonStats.zScore > last.referenceValueComparisonStats.zScore) ? first : last;
	};

	const findingDataEntries = insight.findingData ? Object.entries<FindingDataItem>(insight.findingData).map(([key, value]) => {
		const bestSegment = getBestSegment(value?.history?.segmentsStats);
		return { key, value, bestSegment };
	}) : [];

	const parseInterval = (intervalStr: string | null) => {
		if (!intervalStr) return undefined;
		const [startStr, endStr] = intervalStr.split(' - ');
		return { start: moment(startStr, 'D MMM'), end: moment(endStr, 'D MMM') };
	};

	return (
		<Article innerSize='l'>
			<CardHeader insight={insight} />
			<div className={styles.metricList}>
				{findingDataEntries.map(({ key, value, bestSegment }, index) => {
					const historyDepth = bestSegment?.historyDepth;
					const labels = getIntervalLabelsForData(
						range.firstPeriode.end, 
						insight.period, 
						value?.history?.values?.length || 0, 
						historyDepth
					);
					
					const comparisonInterval = getComparisonInterval(
						range.firstPeriode.end,
						insight.period,
						value?.history?.values?.length || 0,
						historyDepth
					);
					
					const intervalBlue = comparisonInterval 
						? `${getFormattedDate(comparisonInterval.start)} - ${getFormattedDate(comparisonInterval.end)}`
						: null;
					const intervalGreen = labels.length > 0 ? labels[labels.length - 1] : null;

					return (
						<Fragment key={key}>
							<div className={styles.sentence}>
								<SentenceGenerator insight={insight} findingKey={key} value={value} bestSegment={bestSegment} />	
							</div>
							<div className={styles.metricItem} key={index}>
								<div className={styles.metricItemColValue}>
									<Metric
										value={value.absolute}
										prevValue={bestSegment ? bestSegment.mean : 0}
										evolution={bestSegment ? bestSegment.referenceValueComparisonStats.variation : 0}
										isFavorable={value.favorableness}
										findingType={insight.findingType}
										translatedDescription={value.translatedDescription}
									/>
								</div>
								<div className={styles.metricItemColChart}>
									{insight.period !== 'LastDay' && chartType === 'line'  && 
										<MiniChart 
											history={value.history}
											type={value.absoluteType}
											period={range}
											labels={labels}
											historyDepth={historyDepth}
										/>
									}
									{insight.period !== 'LastDay' && chartType === 'area' && 
										<MiniChart2 
											history={value.history}
											type={value.absoluteType}
											period={range}
											labels={labels}
											historyDepth={historyDepth}
										/>
									}
									{insight.period !== 'LastDay' && chartType === 'bar' && 
										<MiniBarChart 
											history={value.history}
											type={value.absoluteType}
											period={range}
											labels={labels}
											historyDepth={historyDepth}
										/>
									}
									{insight.period === 'LastDay' && 
										<MiniBarChart 
											history={value.history}
											type={value.absoluteType}
											period={range}
											labels={labels}
											historyDepth={historyDepth}
										/>
									}
									{(bestSegment ? bestSegment.referenceValueComparisonStats.variation : value.relative) !== 100 &&
										<TableMetric
											prevValue={bestSegment ? bestSegment.mean : 0}
											value={value.absolute}
											type={value.absoluteType}
											isFavorable={value.favorableness}
											intervalBlue={parseInterval(intervalBlue)}
											intervalGreen={parseInterval(intervalGreen)}
										/>
									}
								</div>
							</div>
						</Fragment>
					);
				})}
			</div>
		</Article>
	);
};

export default InsightsCard;
